/*
  wall-preview.js - "See it on your wall" (replaces the BlankWall app block).

  1. Photo mode (every browser, incl. Instagram/Facebook in-app): the shopper takes or picks a
     photo of their wall; the framed poster is drawn on top. Drag to move, pinch / wheel to set
     the wall scale. Size and frame can be switched; all sizes share one cm scale, so switching
     size shows the real difference. "Save" shares or downloads the picture, "Choose" applies the
     size + frame to the product form.
  2. AR mode (only where it works: iOS Safari Quick Look, Android Chrome WebXR): a GLB with the
     framed poster at real size is built in the browser and shown with <model-viewer>
     (ar-placement="wall"); iOS gets its USDZ generated by model-viewer.

  Config: JSON in [data-wall-config], rendered by snippets/wall-preview.liquid.
  Any element with href="#wall-preview" opens the tool.
  Events: window.dataLayer.push({event: 'wall_preview', wall_action}) for open/photo/save/apply/ar.
*/
(() => {
  if (customElements.get('wall-preview')) return;

  const STR = {
    da: {
      title: 'Se plakaten på din væg',
      intro: 'Tag et billede af din væg, og flyt plakaten på plads. Skift størrelse og ramme, og se forskellen med det samme.',
      camera: 'Tag et billede af din væg',
      libraryDesk: 'Vælg et billede af din væg',
      library: 'Vælg et billede',
      ar: 'Se den live på din væg med kameraet',
      arOpen: 'Åbn kameraet og se den på væggen',
      arHint: 'Peg kameraet mod væggen. Plakaten vises i den rigtige størrelse.',
      arPreparing: 'Gør kameravisningen klar …',
      arFailed: 'Kameravisningen virker ikke på denne telefon. Prøv med et billede af din væg i stedet.',
      hintMouse: 'Træk for at flytte. Scroll for at ændre målestok.',
      cropNote: 'Bemærk: {size} har et lidt andet format end billedet her, så visningen kan beskære motivet forkert. Din plakat bliver tilpasset formatet med omhu, så motivet står helt og flot.',
      hint: 'Træk for at flytte. Knib for at ændre målestok.',
      scale: 'Billedet viser ca. {m} m væg i bredden',
      scaleTip: 'Passer målestokken? En dør er ca. 2 m høj.',
      size: 'Størrelse',
      frame: 'Ramme',
      none: 'Ingen ramme',
      noFrameSize: 'Leveres uden ramme',
      save: 'Gem billede',
      apply: 'Tilføj til kurv · {size}',
      newPhoto: 'Nyt billede',
      back: 'Tilbage',
      close: 'Luk',
      loadError: 'Billedet kunne ikke indlæses. Prøv et andet.',
      soldOut: 'udsolgt'
    },
    en: {
      title: 'See the art print on your wall',
      intro: 'Take a photo of your wall and move the art print into place. Switch size and frame and see the difference right away.',
      camera: 'Take a photo of your wall',
      libraryDesk: 'Choose a photo of your wall',
      library: 'Choose a photo',
      ar: 'See it live on your wall with your camera',
      arOpen: 'Open the camera and see it on your wall',
      arHint: 'Point your camera at the wall. The art print is shown at its real size.',
      arPreparing: 'Getting the camera view ready …',
      arFailed: 'The camera view does not work on this phone. Try with a photo of your wall instead.',
      hintMouse: 'Drag to move. Scroll to change the scale.',
      cropNote: 'Note: {size} has a slightly different format than this image, so the preview may crop the artwork. Your print is carefully adapted to the format, so the artwork looks complete and beautiful.',
      hint: 'Drag to move. Pinch to change the scale.',
      scale: 'The photo shows about {m} m of wall',
      scaleTip: 'Does the scale look right? A door is about 2 m tall.',
      size: 'Size',
      frame: 'Frame',
      none: 'No frame',
      noFrameSize: 'Delivered without frame',
      save: 'Save image',
      apply: 'Add to cart · {size}',
      newPhoto: 'New photo',
      back: 'Back',
      close: 'Close',
      loadError: 'The photo could not be loaded. Try another one.',
      soldOut: 'sold out'
    },
    de: {
      title: 'Sieh das Poster an deiner Wand',
      intro: 'Mach ein Foto von deiner Wand und schieb das Poster an seinen Platz. Wechsle Größe und Rahmen und sieh den Unterschied sofort.',
      camera: 'Foto von deiner Wand machen',
      libraryDesk: 'Foto deiner Wand auswählen',
      library: 'Foto auswählen',
      ar: 'Mit der Kamera live an deiner Wand ansehen',
      arOpen: 'Kamera öffnen und an der Wand ansehen',
      arHint: 'Richte die Kamera auf die Wand. Das Poster wird in echter Größe gezeigt.',
      arPreparing: 'Kameraansicht wird vorbereitet …',
      arFailed: 'Die Kameraansicht funktioniert auf diesem Handy nicht. Versuch es mit einem Foto deiner Wand.',
      hintMouse: 'Ziehen zum Verschieben. Scrollen ändert den Maßstab.',
      cropNote: 'Hinweis: {size} hat ein etwas anderes Format als dieses Bild, daher kann die Vorschau das Motiv falsch zuschneiden. Dein Poster wird sorgfältig an das Format angepasst, sodass das Motiv vollständig und schön zur Geltung kommt.',
      hint: 'Ziehen zum Verschieben. Mit zwei Fingern den Maßstab ändern.',
      scale: 'Das Foto zeigt ca. {m} m Wand in der Breite',
      scaleTip: 'Stimmt der Maßstab? Eine Tür ist ca. 2 m hoch.',
      size: 'Größe',
      frame: 'Rahmen',
      none: 'Ohne Rahmen',
      noFrameSize: 'Wird ohne Rahmen geliefert',
      save: 'Bild speichern',
      apply: 'In den Warenkorb · {size}',
      newPhoto: 'Neues Foto',
      back: 'Zurück',
      close: 'Schließen',
      loadError: 'Das Foto konnte nicht geladen werden. Versuch ein anderes.',
      soldOut: 'ausverkauft'
    },
    fr: {
      title: 'Voir l’affiche sur votre mur',
      intro: 'Prenez votre mur en photo et placez l’affiche. Changez de format et de cadre et voyez la différence tout de suite.',
      camera: 'Prendre votre mur en photo',
      libraryDesk: 'Choisir une photo de votre mur',
      library: 'Choisir une photo',
      ar: 'Voir en direct sur votre mur avec l’appareil photo',
      arOpen: 'Ouvrir l’appareil photo et voir sur le mur',
      arHint: 'Pointez l’appareil photo vers le mur. L’affiche est montrée à sa taille réelle.',
      arPreparing: 'Préparation de la vue caméra …',
      arFailed: 'La vue caméra ne fonctionne pas sur ce téléphone. Essayez avec une photo de votre mur.',
      hintMouse: 'Glissez pour déplacer. Faites défiler pour changer l’échelle.',
      cropNote: 'Remarque : le format {size} diffère légèrement de cette image, l’aperçu peut donc mal recadrer l’œuvre. Votre affiche est soigneusement adaptée au format pour que l’œuvre soit complète et belle.',
      hint: 'Glissez pour déplacer. Pincez pour changer l’échelle.',
      scale: 'La photo montre environ {m} m de mur en largeur',
      scaleTip: 'L’échelle est-elle juste ? Une porte mesure environ 2 m.',
      size: 'Format',
      frame: 'Cadre',
      none: 'Sans cadre',
      noFrameSize: 'Livré sans cadre',
      save: 'Enregistrer l’image',
      apply: 'Ajouter au panier · {size}',
      newPhoto: 'Nouvelle photo',
      back: 'Retour',
      close: 'Fermer',
      loadError: 'La photo n’a pas pu être chargée. Essayez-en une autre.',
      soldOut: 'épuisé'
    }
  };

  const A_SIZES = { A5: [15, 21], A4: [21, 30], A3: [30, 42], A2: [42, 59], A1: [59, 84], A0: [84, 119] };
  const MODEL_VIEWER = 'https://cdn.jsdelivr.net/npm/@google/model-viewer@3.5.0/dist/model-viewer.min.js';
  const DEFAULT_WALL_CM = 250; // a phone photo of a wall from 2-3 m away covers roughly this width
  const MIN_WALL_CM = 60;
  const MAX_WALL_CM = 1200;

  const track = (action) => {
    try {
      (window.dataLayer = window.dataLayer || []).push({ event: 'wall_preview', wall_action: action });
    } catch (e) {}
  };

  /** Size option value ("A3", "50x70", "100 x 140") -> [w, h] in cm, or null. */
  const parseCm = (value) => {
    const v = String(value || '').trim();
    const a = A_SIZES[v.toUpperCase()];
    if (a) return a.slice();
    const m = v.match(/(\d+(?:[.,]\d+)?)\s*[x×]\s*(\d+(?:[.,]\d+)?)/i);
    if (!m) return null;
    return [parseFloat(m[1].replace(',', '.')), parseFloat(m[2].replace(',', '.'))];
  };

  /** Shopify CDN URLs on the shop's own domain -> same-origin URL, so canvases stay untainted. */
  const sameOrigin = (url) => {
    try {
      const u = new URL(url, location.href);
      if (u.pathname.startsWith('/cdn/')) return location.origin + u.pathname + u.search;
      return u.href;
    } catch (e) {
      return url;
    }
  };

  const loadImage = (src) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.decoding = 'async';
      // Cross-origin images (e.g. cdn.shopify.com) must be CORS-loaded, or the canvas is tainted
      // and neither "save" nor the AR model can export it.
      try {
        if (new URL(src, location.href).origin !== location.origin) img.crossOrigin = 'anonymous';
      } catch (e) {}
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });

  const el = (tag, cls, attrs) => {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (attrs) for (const k in attrs) {
      if (k === 'text') n.textContent = attrs[k];
      else n.setAttribute(k, attrs[k]);
    }
    return n;
  };

  /** Draws img as a 9-slice frame (border-image, stretch, no fill) on rect x,y,w,h. */
  const drawNineSlice = (ctx, img, s, d, x, y, w, h) => {
    const iw = img.naturalWidth;
    const ih = img.naturalHeight;
    const [st, sr, sb, sl] = s;
    const [dt, dr, db, dl] = d;
    const smw = iw - sl - sr;
    const smh = ih - st - sb;
    const dmw = w - dl - dr;
    const dmh = h - dt - db;
    // corners
    ctx.drawImage(img, 0, 0, sl, st, x, y, dl, dt);
    ctx.drawImage(img, iw - sr, 0, sr, st, x + w - dr, y, dr, dt);
    ctx.drawImage(img, 0, ih - sb, sl, sb, x, y + h - db, dl, db);
    ctx.drawImage(img, iw - sr, ih - sb, sr, sb, x + w - dr, y + h - db, dr, db);
    // edges
    ctx.drawImage(img, sl, 0, smw, st, x + dl, y, dmw, dt);
    ctx.drawImage(img, sl, ih - sb, smw, sb, x + dl, y + h - db, dmw, db);
    ctx.drawImage(img, 0, st, sl, smh, x, y + dt, dl, dmh);
    ctx.drawImage(img, iw - sr, st, sr, smh, x + w - dr, y + dt, dr, dmh);
  };

  /** Draws img object-fit: cover into rect. */
  const drawCover = (ctx, img, x, y, w, h) => {
    const iw = img.naturalWidth || img.width;
    const ih = img.naturalHeight || img.height;
    const s = Math.max(w / iw, h / ih);
    const sw = w / s;
    const sh = h / s;
    ctx.drawImage(img, (iw - sw) / 2, (ih - sh) / 2, sw, sh, x, y, w, h);
  };

  /* ---------- GLB: framed poster as real 3D (metres, +Z faces the room, back at z = 0) ---------- */
  const srgbToLinear = (c) => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  const hexToLinear = (hex) => {
    const m = String(hex || '').match(/^#?([0-9a-f]{6})$/i);
    const n = m ? parseInt(m[1], 16) : 0x222222;
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => srgbToLinear(v / 255)).concat(1);
  };

  /** Geometry bucket per material: flat arrays + quad helper. */
  const makeGeom = () => {
    const g = { pos: [], nrm: [], uv: [], idx: [] };
    g.quad = (pts, n, uvOf) => {
      const base = g.pos.length / 3;
      for (const p of pts) {
        g.pos.push(p[0], p[1], p[2]);
        g.nrm.push(n[0], n[1], n[2]);
        const uv = uvOf ? uvOf(p) : [0, 0];
        g.uv.push(uv[0], uv[1]);
      }
      g.idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
    };
    /** Axis-aligned box; the +Z face goes to `front` (textured), the rest to `side`. */
    g.box = (front, side, x0, x1, y0, y1, z0, z1, uvOf) => {
      front.quad([[x0, y0, z1], [x1, y0, z1], [x1, y1, z1], [x0, y1, z1]], [0, 0, 1], uvOf);
      side.quad([[x1, y0, z0], [x0, y0, z0], [x0, y1, z0], [x1, y1, z0]], [0, 0, -1]);
      side.quad([[x1, y0, z1], [x1, y0, z0], [x1, y1, z0], [x1, y1, z1]], [1, 0, 0]);
      side.quad([[x0, y0, z0], [x0, y0, z1], [x0, y1, z1], [x0, y1, z0]], [-1, 0, 0]);
      side.quad([[x0, y1, z1], [x1, y1, z1], [x1, y1, z0], [x0, y1, z0]], [0, 1, 0]);
      side.quad([[x0, y0, z0], [x1, y0, z0], [x1, y0, z1], [x0, y0, z1]], [0, -1, 0]);
    };
    return g;
  };

  /**
   * opts: {w, h} outer size in m, band = frame profile width in m (0 = unframed),
   * depth = frame depth in m, swatch = frame colour (sides of the moulding).
   * The composite canvas (poster + painted frame front) textures every front face, so the
   * moulding front keeps its wood/black texture and the sides get the frame colour.
   */
  const buildGlb = async (canvas, opts) => {
    const jpeg = await new Promise((r) => canvas.toBlob(r, 'image/jpeg', 0.9));
    const img = new Uint8Array(await jpeg.arrayBuffer());
    const W = opts.w;
    const H = opts.h;
    const hw = W / 2;
    const hh = H / 2;
    const uvOf = (p) => [(p[0] + hw) / W, 1 - (p[1] + hh) / H];
    const front = makeGeom();
    const side = makeGeom();
    const back = makeGeom();
    const materials = [
      { name: 'print', pbrMetallicRoughness: { baseColorTexture: { index: 0 }, metallicFactor: 0, roughnessFactor: 0.85 } }
    ];
    if (opts.band > 0) {
      const b = opts.band;
      const D = opts.depth;
      const recess = Math.min(D * 0.45, 0.009); // print sits behind the moulding front
      // moulding: top, bottom (full width), left, right (between them)
      front.box(front, side, -hw, hw, hh - b, hh, 0, D, uvOf);
      front.box(front, side, -hw, hw, -hh, -hh + b, 0, D, uvOf);
      front.box(front, side, -hw, -hw + b, -hh + b, hh - b, 0, D, uvOf);
      front.box(front, side, hw - b, hw, -hh + b, hh - b, 0, D, uvOf);
      // print (inner opening) recessed, backing board behind everything
      front.quad([[-hw + b, -hh + b, D - recess], [hw - b, -hh + b, D - recess], [hw - b, hh - b, D - recess], [-hw + b, hh - b, D - recess]], [0, 0, 1], uvOf);
      back.quad([[hw, -hh, 0], [-hw, -hh, 0], [-hw, hh, 0], [hw, hh, 0]], [0, 0, -1]);
      materials.push({ name: 'moulding', pbrMetallicRoughness: { baseColorFactor: hexToLinear(opts.swatch), metallicFactor: 0, roughnessFactor: 0.7 } });
    } else {
      // unframed print: a thin sheet with paper-white edges
      front.box(front, side, -hw, hw, -hh, hh, 0, 0.002, uvOf);
      materials.push({ name: 'paper-edge', pbrMetallicRoughness: { baseColorFactor: hexToLinear('#f4f2ee'), metallicFactor: 0, roughnessFactor: 0.9 } });
    }
    materials.push({ name: 'back', pbrMetallicRoughness: { baseColorFactor: hexToLinear('#3a3734'), metallicFactor: 0, roughnessFactor: 1 } });

    const groups = [
      { g: front, material: 0 },
      { g: side, material: 1 },
      { g: back, material: 2 }
    ].filter((x) => x.g.idx.length);

    const pad4 = (n) => (n + 3) & ~3;
    const chunks = [];
    const bufferViews = [];
    const accessors = [];
    let len = 0;
    const addView = (arr, target) => {
      chunks.push({ arr, offset: len });
      bufferViews.push(Object.assign({ buffer: 0, byteOffset: len, byteLength: arr.byteLength }, target ? { target } : {}));
      len = pad4(len + arr.byteLength);
      return bufferViews.length - 1;
    };
    const primitives = groups.map(({ g, material }) => {
      const pos = new Float32Array(g.pos);
      const min = [Infinity, Infinity, Infinity];
      const max = [-Infinity, -Infinity, -Infinity];
      for (let i = 0; i < pos.length; i += 3) {
        for (let k = 0; k < 3; k++) {
          min[k] = Math.min(min[k], pos[i + k]);
          max[k] = Math.max(max[k], pos[i + k]);
        }
      }
      const count = pos.length / 3;
      accessors.push({ bufferView: addView(pos, 34962), componentType: 5126, count, type: 'VEC3', min, max });
      const p = accessors.length - 1;
      accessors.push({ bufferView: addView(new Float32Array(g.nrm), 34962), componentType: 5126, count, type: 'VEC3' });
      const n = accessors.length - 1;
      accessors.push({ bufferView: addView(new Float32Array(g.uv), 34962), componentType: 5126, count, type: 'VEC2' });
      const t = accessors.length - 1;
      accessors.push({ bufferView: addView(new Uint16Array(g.idx), 34963), componentType: 5123, count: g.idx.length, type: 'SCALAR' });
      return { attributes: { POSITION: p, NORMAL: n, TEXCOORD_0: t }, indices: accessors.length - 1, material };
    });
    const imageView = addView(img);

    const bin = new Uint8Array(len);
    for (const c of chunks) bin.set(new Uint8Array(c.arr.buffer, c.arr.byteOffset, c.arr.byteLength), c.offset);
    const json = {
      asset: { version: '2.0', generator: 'peleton wall-preview' },
      scene: 0,
      scenes: [{ nodes: [0] }],
      nodes: [{ mesh: 0, name: 'poster' }],
      meshes: [{ primitives }],
      materials,
      textures: [{ source: 0, sampler: 0 }],
      samplers: [{ magFilter: 9729, minFilter: 9729, wrapS: 33071, wrapT: 33071 }],
      images: [{ bufferView: imageView, mimeType: 'image/jpeg' }],
      buffers: [{ byteLength: len }],
      bufferViews,
      accessors
    };
    const jsonBytes = new TextEncoder().encode(JSON.stringify(json));
    const jsonLen = pad4(jsonBytes.length);
    const total = 12 + 8 + jsonLen + 8 + len;
    const out = new ArrayBuffer(total);
    const dv = new DataView(out);
    dv.setUint32(0, 0x46546c67, true); // glTF
    dv.setUint32(4, 2, true);
    dv.setUint32(8, total, true);
    dv.setUint32(12, jsonLen, true);
    dv.setUint32(16, 0x4e4f534a, true); // JSON
    const jsonChunk = new Uint8Array(out, 20, jsonLen);
    jsonChunk.fill(0x20);
    jsonChunk.set(jsonBytes);
    dv.setUint32(20 + jsonLen, len, true);
    dv.setUint32(24 + jsonLen, 0x004e4942, true); // BIN
    new Uint8Array(out, 28 + jsonLen, len).set(bin);
    return new Blob([out], { type: 'model/gltf-binary' });
  };

  let modelViewerPromise = null;
  const loadModelViewer = () => {
    if (customElements.get('model-viewer')) return Promise.resolve();
    if (!modelViewerPromise) {
      modelViewerPromise = new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.type = 'module';
        s.src = MODEL_VIEWER;
        s.onload = () => customElements.whenDefined('model-viewer').then(resolve);
        s.onerror = reject;
        document.head.appendChild(s);
      });
    }
    return modelViewerPromise;
  };

  /** Quick Look (iOS Safari, not in-app web views) or WebXR immersive-ar (Android Chrome). */
  const arSupport = async () => {
    const a = document.createElement('a');
    if (a.relList && a.relList.supports && a.relList.supports('ar')) return 'quick-look';
    try {
      if (navigator.xr && (await navigator.xr.isSessionSupported('immersive-ar'))) return 'webxr';
    } catch (e) {}
    return null;
  };

  class WallPreview extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      try {
        this.cfg = JSON.parse(this.querySelector('[data-wall-config]').textContent);
      } catch (e) {
        return;
      }
      const lang = (this.dataset.locale || document.documentElement.lang || 'da').slice(0, 2);
      this.t = STR[lang] || STR.en;
      this.cfg.frames = (this.cfg.frames || []).filter((f) => f.key);
      this.unframed = String(this.cfg.unframedSizes || '')
        .split(',')
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean);
      this.sizes = this.buildSizes();
      this.querySelector('[data-wall-open]').addEventListener('click', () => this.open());
      if (!window.__wallPreviewLinks) {
        window.__wallPreviewLinks = true;
        document.addEventListener('click', (e) => {
          const a = e.target.closest('a[href="#wall-preview"]');
          const wp = a && document.querySelector('wall-preview');
          if (!wp) return;
          e.preventDefault();
          wp.open();
        });
      }
      arSupport().then((mode) => (this.arMode = mode));
    }

    tpl(key, vars) {
      let s = this.t[key] || '';
      for (const k in vars || {}) s = s.replace('{' + k + '}', vars[k]);
      return s;
    }

    /** One entry per size option value, in variant order. */
    buildSizes() {
      const seen = new Map();
      for (const v of this.cfg.variants || []) {
        if (seen.has(v.value)) {
          if (v.available) seen.get(v.value).available = true;
          continue;
        }
        const cm = parseCm(v.value);
        if (!cm) continue;
        seen.set(v.value, { value: v.value, cm, id: v.id, available: v.available });
      }
      return [...seen.values()];
    }

    label(size) {
      return /^A\d$/i.test(size.value) ? size.value.toUpperCase() : `${size.cm[0]}×${size.cm[1]}`;
    }

    /* ---------- PDP state ---------- */
    currentSize() {
      const input = document.querySelector('form[action*="/cart/add"] [name="id"]');
      const id = input ? Number(input.value) : null;
      const v = (this.cfg.variants || []).find((x) => x.id === id);
      return (v && this.sizes.find((s) => s.value === v.value)) || this.sizes.find((s) => s.available) || this.sizes[0];
    }

    /** Frame key for a frame-selector swatch handle. Handles are translated per market
        (ww: "lys-eiche-rahmen"), so ask the theme's PeletonFrames first. */
    keyOf(handle) {
      if (!handle || handle === 'none') return 'none';
      const pf = window.PeletonFrames;
      const k = pf && pf.keyForHandle ? pf.keyForHandle(handle) : null;
      if (k) return k;
      const f = this.cfg.frames.find((x) => x.handle === handle);
      return f ? f.key : null;
    }

    currentFrame() {
      const sw = document.querySelector('.frame-selector__swatch[aria-checked="true"]');
      return (sw && this.keyOf(sw.getAttribute('data-frame'))) || 'none';
    }

    frameAllowed(size) {
      const key = `${size.cm[0]}x${size.cm[1]}`;
      if (!this.unframed.includes(key) && !this.unframed.includes(size.value.toLowerCase())) return true;
      return document.documentElement.getAttribute('data-oversized-frames') === 'true';
    }

    /* ---------- modal ---------- */
    open() {
      track('open');
      this.state = this.state || {};
      this.state.size = this.currentSize();
      this.state.frame = this.currentFrame();
      if (!this.frameAllowed(this.state.size)) this.state.frame = 'none';
      if (!this.modal) this.buildModal();
      this.modal.hidden = false;
      document.documentElement.classList.add('wall-preview-lock');
      this.showStart();
      try {
        history.pushState({ wallPreview: true }, '');
        this._pushed = true;
      } catch (e) {}
      this._onPop = () => {
        this._pushed = false;
        this.close();
      };
      window.addEventListener('popstate', this._onPop);
      this.preload();
    }

    close() {
      if (!this.modal || this.modal.hidden) return;
      this.modal.hidden = true;
      document.documentElement.classList.remove('wall-preview-lock');
      window.removeEventListener('popstate', this._onPop);
      if (this._pushed) {
        this._pushed = false;
        history.back();
      }
      if (this.mv) {
        this.mv.remove();
        this.mv = null;
      }
    }

    preload() {
      if (!this._assets) {
        const frames = this.cfg.frames.filter((f) => f.overlay);
        this._assets = Promise.all([
          loadImage(sameOrigin(this.cfg.poster.url)),
          ...frames.map((f) => loadImage(sameOrigin(f.overlay)).catch(() => null))
        ]).then(([poster, ...overlays]) => {
          this.posterImg = poster;
          this.overlays = {};
          frames.forEach((f, i) => overlays[i] && (this.overlays[f.key] = overlays[i]));
        });
      }
      return this._assets;
    }

    buildModal() {
      const t = this.t;
      const m = el('div', 'wall-preview__modal', { role: 'dialog', 'aria-modal': 'true', 'aria-label': t.title });
      m.hidden = true;
      m.innerHTML = `
        <div class="wall-preview__bar">
          <button type="button" class="wall-preview__icon-btn" data-wp-back aria-label="${t.back}" hidden>
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M15 5l-7 7 7 7"/></svg>
          </button>
          <span class="wall-preview__bar-title"></span>
          <button type="button" class="wall-preview__icon-btn" data-wp-close aria-label="${t.close}">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M6 6l12 12M18 6L6 18"/></svg>
          </button>
        </div>
        <div class="wall-preview__start" data-wp-start>
          <div class="wall-preview__start-art"><canvas data-wp-thumb></canvas></div>
          <h2 class="wall-preview__title">${t.title}</h2>
          <p class="wall-preview__intro">${t.intro}</p>
          <div class="wall-preview__actions">
            <label class="wall-preview__btn wall-preview__btn--primary" data-wp-camera>
              <input type="file" accept="image/*" capture="environment" data-wp-file hidden>
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M4 8h3l2-3h6l2 3h3v11H4z"/><circle cx="12" cy="13" r="3.5"/></svg>
              <span>${t.camera}</span>
            </label>
            <label class="wall-preview__btn" data-wp-library>
              <input type="file" accept="image/*" data-wp-file hidden>
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.6"><rect x="3.5" y="5.5" width="17" height="13"/><path d="M3.5 15l5-4 4 3 3-2 5 4"/></svg>
              <span>${matchMedia('(pointer: fine)').matches ? t.libraryDesk : t.library}</span>
            </label>
            <button type="button" class="wall-preview__btn" data-wp-ar hidden>
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M4 8h3l2-3h6l2 3h3v11H4z"/><circle cx="12" cy="13" r="3.5"/><path d="M18.5 3.5l1.5 1.5M20.5 2.5v2"/></svg>
              <span>${t.ar}</span>
            </button>
          </div>
          <p class="wall-preview__error" data-wp-error hidden></p>
        </div>
        <div class="wall-preview__editor" data-wp-editor hidden>
          <div class="wall-preview__stage" data-wp-stage>
            <canvas data-wp-canvas></canvas>
            <span class="wall-preview__hint" data-wp-hint>${matchMedia('(pointer: fine)').matches ? t.hintMouse : t.hint}</span>
          </div>
          <div class="wall-preview__panel">
            <div class="wall-preview__scale"><span data-wp-scale></span><span class="wall-preview__scale-tip">${t.scaleTip}</span></div>
            <div class="wall-preview__row">
              <span class="wall-preview__row-label">${t.size}</span>
              <div class="wall-preview__chips" data-wp-sizes></div>
            </div>
            <div class="wall-preview__row" data-wp-frame-row>
              <span class="wall-preview__row-label">${t.frame}</span>
              <div class="wall-preview__chips" data-wp-frames></div>
            </div>
            <p class="wall-preview__note" data-wp-note hidden></p>
            <div class="wall-preview__cta">
              <button type="button" class="wall-preview__btn" data-wp-save>${t.save}</button>
              <button type="button" class="wall-preview__btn wall-preview__btn--primary" data-wp-apply></button>
            </div>
          </div>
        </div>
        <div class="wall-preview__ar" data-wp-arview hidden>
          <div class="wall-preview__ar-stage" data-wp-arstage></div>
          <button type="button" class="wall-preview__btn wall-preview__btn--primary wall-preview__btn--ar" data-wp-ar-open hidden>
            <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" aria-hidden="true"><path d="M4 8h3l2-3h6l2 3h3v11H4z"/><circle cx="12" cy="13" r="3.5"/><path d="M18.5 3.5l1.5 1.5M20.5 2.5v2"/></svg>
            <span data-wp-ar-open-label></span>
          </button>
          <p class="wall-preview__intro wall-preview__ar-status" data-wp-arstatus></p>
          <div class="wall-preview__panel wall-preview__panel--ar">
            <div class="wall-preview__row">
              <span class="wall-preview__row-label">${t.size}</span>
              <div class="wall-preview__chips" data-wp-ar-sizes></div>
            </div>
            <div class="wall-preview__row">
              <span class="wall-preview__row-label">${t.frame}</span>
              <div class="wall-preview__chips" data-wp-ar-frames></div>
            </div>
            <p class="wall-preview__note" data-wp-note hidden></p>
            <div class="wall-preview__cta wall-preview__cta--single">
              <button type="button" class="wall-preview__btn" data-wp-ar-apply></button>
            </div>
          </div>
        </div>`;
      if (matchMedia('(pointer: fine)').matches) m.classList.add('is-pointer-fine');
      document.body.appendChild(m);
      this.modal = m;
      const q = (s) => m.querySelector(s);
      this.$ = {
        start: q('[data-wp-start]'),
        editor: q('[data-wp-editor]'),
        arView: q('[data-wp-arview]'),
        arStage: q('[data-wp-arstage]'),
        arStatus: q('[data-wp-arstatus]'),
        back: q('[data-wp-back]'),
        barTitle: q('.wall-preview__bar-title'),
        stage: q('[data-wp-stage]'),
        canvas: q('[data-wp-canvas]'),
        thumb: q('[data-wp-thumb]'),
        hint: q('[data-wp-hint]'),
        scale: q('[data-wp-scale]'),
        sizes: q('[data-wp-sizes]'),
        frames: q('[data-wp-frames]'),
        frameRow: q('[data-wp-frame-row]'),
        arSizes: q('[data-wp-ar-sizes]'),
        arFrames: q('[data-wp-ar-frames]'),
        arApply: q('[data-wp-ar-apply]'),
        notes: m.querySelectorAll('[data-wp-note]'),
        arOpen: q('[data-wp-ar-open]'),
        arOpenLabel: q('[data-wp-ar-open-label]'),
        apply: q('[data-wp-apply]'),
        error: q('[data-wp-error]'),
        ar: q('[data-wp-ar]')
      };
      q('[data-wp-close]').addEventListener('click', () => this.close());
      // Desktop: the modal layer itself is the dimmed backdrop around the card - a click there closes.
      m.addEventListener('click', (e) => e.target === m && this.close());
      this.$.back.addEventListener('click', () => this.showStart());
      m.querySelectorAll('[data-wp-file]').forEach((input) =>
        input.addEventListener('change', () => {
          const f = input.files && input.files[0];
          input.value = '';
          if (f) this.usePhoto(f);
        })
      );
      this.$.ar.addEventListener('click', () => this.startAR());
      // activateAR() must run inside the tap (iOS Quick Look needs the user gesture).
      this.$.arOpen.addEventListener('click', () => {
        track('ar_open');
        if (this.mv && this.mv.canActivateAR) this.mv.activateAR();
      });
      q('[data-wp-save]').addEventListener('click', () => this.save());
      this.$.apply.addEventListener('click', () => this.apply());
      this.$.arApply.addEventListener('click', () => this.apply());
      document.addEventListener('keydown', (e) => e.key === 'Escape' && this.close());
      this.bindGestures();
      new ResizeObserver(() => !this.$.editor.hidden && this.layout()).observe(this.$.stage);
    }

    showStart() {
      this.$.start.hidden = false;
      this.$.editor.hidden = true;
      this.$.arView.hidden = true;
      this.$.back.hidden = true;
      this.$.barTitle.textContent = '';
      this.$.error.hidden = true;
      this.$.ar.hidden = !this.arMode;
      if (this.mv) {
        this.mv.remove();
        this.mv = null;
      }
      this.preload().then(() => this.drawThumb());
    }

    drawThumb() {
      const c = this.$.thumb;
      const art = this.compose(this.state.size, this.state.frame, 640);
      c.width = art.width;
      c.height = art.height;
      c.getContext('2d').drawImage(art, 0, 0);
    }

    /* ---------- framed poster composite ---------- */
    /** Poster [w, h] in cm, oriented like the image (landscape images get w > h). */
    /**
     * Poster [w, h] in cm as shown. The artwork is NEVER cropped, except for the sizes listed in
     * cfg.cropSizes (40x50): every other size takes the artwork's own ratio with the size's long
     * side, so the whole motif is visible (30x40 on a 5:7 artwork -> 28.6 x 40).
     */
    posterCm(size) {
      let [w, h] = size.cm;
      const p = this.cfg.poster;
      const landscape = p.width && p.height ? p.width > p.height : false;
      if (landscape !== w > h && w !== h) [w, h] = [h, w];
      if (this.cropsArtwork(size) || !p.width || !p.height) return [w, h];
      const long = Math.max(w, h);
      const r = p.width / p.height;
      return r >= 1 ? [long, Math.round((long / r) * 10) / 10] : [Math.round(long * r * 10) / 10, long];
    }

    /** Real frame moulding width in cm (config frameCm: default + wider profile for large sizes). */
    bandCm(size) {
      const f = this.cfg.frameCm || {};
      const large = String(f.largeSizes || '')
        .split(',')
        .map((x) => x.trim().toLowerCase().replace(/\s+/g, ''))
        .filter(Boolean);
      const key = `${size.cm[0]}x${size.cm[1]}`;
      const isLarge = large.includes(String(size.value).toLowerCase().replace(/\s+/g, '')) || large.includes(key);
      return Number(isLarge ? f.large : f.default) || 1.5;
    }

    outerCm(size, frame) {
      const [w, h] = this.posterCm(size);
      if (frame === 'none' || !this.frameAllowed(size)) return [w, h];
      const b = this.bandCm(size);
      return [w + 2 * b, h + 2 * b];
    }

    /** Returns a canvas with the poster (+ frame), longest side = maxPx. Cached per size/frame. */
    compose(size, frame, maxPx) {
      const key = `${size.value}|${frame}|${maxPx}`;
      this._cache = this._cache || {};
      if (this._cache[key]) return this._cache[key];
      const framed = frame !== 'none' && this.frameAllowed(size);
      const [ow, oh] = this.outerCm(size, frame);
      const pxCm = maxPx / Math.max(ow, oh);
      const c = document.createElement('canvas');
      c.width = Math.round(ow * pxCm);
      c.height = Math.round(oh * pxCm);
      const ctx = c.getContext('2d');
      ctx.imageSmoothingQuality = 'high';
      if (!framed) {
        drawCover(ctx, this.posterImg, 0, 0, c.width, c.height);
      } else {
        const f = this.cfg.frames.find((x) => x.key === frame) || {};
        const b = this.bandCm(size) * pxCm;
        ctx.fillStyle = f.swatch || '#222';
        ctx.fillRect(0, 0, c.width, c.height);
        drawCover(ctx, this.posterImg, b, b, c.width - 2 * b, c.height - 2 * b);
        const overlay = this.overlays[frame];
        if (overlay) {
          const [px, bandPx] = String(f.slice || '22 20 21 30 / 18').split('/');
          const s = px.trim().split(/\s+/).map(Number);
          const bp = Number(bandPx || 18);
          drawNineSlice(ctx, overlay, s, s.map((v) => (v / bp) * b), 0, 0, c.width, c.height);
        }
      }
      this._cache[key] = c;
      return c;
    }

    /* ---------- photo editor ---------- */
    async usePhoto(file) {
      track('photo');
      let img;
      const url = URL.createObjectURL(file);
      try {
        img = await loadImage(url);
        await this.preload();
      } catch (e) {
        this.$.error.textContent = this.t.loadError;
        this.$.error.hidden = false;
        return;
      }
      // Downscale big camera photos once (keeps drawing fast and memory low on phones).
      const max = 2400;
      const s = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight));
      const photo = document.createElement('canvas');
      photo.width = Math.round(img.naturalWidth * s);
      photo.height = Math.round(img.naturalHeight * s);
      photo.getContext('2d').drawImage(img, 0, 0, photo.width, photo.height);
      URL.revokeObjectURL(url);
      this.photo = photo;
      this.state.pxPerCm = photo.width / DEFAULT_WALL_CM;
      this.state.x = photo.width / 2;
      this.state.y = photo.height * 0.42;
      this.$.start.hidden = true;
      this.$.editor.hidden = false;
      this.$.back.hidden = false;
      this.$.barTitle.textContent = this.cfg.productTitle || '';
      this.$.hint.classList.remove('is-gone');
      this.renderChips();
      this.layout();
    }

    /** Size + frame rows, rendered in both the photo editor and the AR view. */
    renderChips() {
      this.fillRows(this.$.sizes, this.$.frames, this.$.apply);
      this.fillRows(this.$.arSizes, this.$.arFrames, this.$.arApply);
      const crops = this.cropsArtwork(this.state.size);
      this.$.notes.forEach((n) => {
        n.hidden = !crops;
        n.textContent = crops ? this.tpl('cropNote', { size: this.label(this.state.size) }) : '';
      });
    }

    /** Sizes where the preview crops the artwork (cfg.cropSizes, e.g. "40x50") - these get the crop note. */
    cropsArtwork(size) {
      const list = String(this.cfg.cropSizes || '')
        .split(',')
        .map((x) => x.trim().toLowerCase().replace(/\s+/g, ''))
        .filter(Boolean);
      return list.includes(String(size.value).toLowerCase().replace(/\s+/g, '')) || list.includes(`${size.cm[0]}x${size.cm[1]}`);
    }

    fillRows(sizesEl, framesEl, applyBtn) {
      const t = this.t;
      sizesEl.innerHTML = '';
      for (const size of this.sizes) {
        const b = el('button', 'wall-preview__chip', { type: 'button', 'aria-pressed': String(size === this.state.size) });
        b.textContent = this.label(size);
        if (!size.available) {
          b.classList.add('is-soldout');
          b.title = t.soldOut;
        }
        b.addEventListener('click', () => {
          this.state.size = size;
          if (!this.frameAllowed(size)) this.state.frame = 'none';
          this.onOptionChange();
        });
        sizesEl.appendChild(b);
      }
      framesEl.innerHTML = '';
      const allowed = this.frameAllowed(this.state.size);
      const opts = [{ key: 'none', label: t.none }, ...(allowed ? this.cfg.frames : [])];
      for (const f of opts) {
        const b = el('button', 'wall-preview__chip wall-preview__chip--frame', { type: 'button', 'aria-pressed': String(f.key === this.state.frame) });
        const dot = el('span', 'wall-preview__swatch');
        if (f.key === 'none') dot.classList.add('is-none');
        else dot.style.background = f.swatch;
        b.append(dot, document.createTextNode(f.key === 'none' && !allowed ? t.noFrameSize : f.label));
        b.addEventListener('click', () => {
          this.state.frame = f.key;
          this.onOptionChange();
        });
        framesEl.appendChild(b);
      }
      for (const row of [sizesEl, framesEl]) {
        const on = row.querySelector('[aria-pressed="true"]');
        if (on) row.scrollLeft = Math.max(0, on.offsetLeft - row.clientWidth / 2 + on.offsetWidth / 2);
      }
      applyBtn.textContent = this.tpl('apply', { size: this.optionLabel() });
      applyBtn.disabled = !this.state.size.available;
    }

    optionLabel() {
      const f = this.cfg.frames.find((x) => x.key === this.state.frame);
      return this.label(this.state.size) + (this.state.frame === 'none' || !f ? '' : ' + ' + f.label);
    }

    onOptionChange() {
      this.renderChips();
      if (!this.$.arView.hidden) this.updateARModel();
      else this.draw();
    }

    layout() {
      const r = this.$.stage.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2.5);
      this.$.canvas.width = Math.round(r.width * dpr);
      this.$.canvas.height = Math.round(r.height * dpr);
      this.$.canvas.style.width = r.width + 'px';
      this.$.canvas.style.height = r.height + 'px';
      this.view = { dpr, w: r.width, h: r.height };
      // Photo is "contained" in the stage.
      const p = this.photo;
      const s = Math.min(r.width / p.width, r.height / p.height);
      this.view.s = s;
      this.view.ox = (r.width - p.width * s) / 2;
      this.view.oy = (r.height - p.height * s) / 2;
      this.draw();
    }

    /** Draws photo + poster. With target canvas (save), draws at photo resolution. */
    draw(target) {
      if (!this.photo) return;
      const p = this.photo;
      const st = this.state;
      const ctx = (target || this.$.canvas).getContext('2d');
      let s;
      let ox;
      let oy;
      if (target) {
        s = 1;
        ox = 0;
        oy = 0;
      } else {
        const v = this.view;
        ctx.setTransform(v.dpr, 0, 0, v.dpr, 0, 0);
        ctx.clearRect(0, 0, v.w, v.h);
        s = v.s;
        ox = v.ox;
        oy = v.oy;
      }
      ctx.drawImage(p, ox, oy, p.width * s, p.height * s);
      const [ow, oh] = this.outerCm(st.size, st.frame);
      const w = ow * st.pxPerCm * s;
      const h = oh * st.pxPerCm * s;
      const x = ox + st.x * s - w / 2;
      const y = oy + st.y * s - h / 2;
      const art = this.compose(st.size, st.frame, target ? 2048 : 1200);
      ctx.save();
      ctx.shadowColor = 'rgba(0,0,0,0.28)';
      ctx.shadowBlur = Math.max(4, w * 0.04);
      ctx.shadowOffsetY = Math.max(2, w * 0.015);
      ctx.drawImage(art, x, y, w, h);
      ctx.restore();
      if (!target) {
        const m = (p.width / st.pxPerCm / 100).toLocaleString(document.documentElement.lang || 'da', { maximumFractionDigits: 1 });
        this.$.scale.textContent = this.tpl('scale', { m });
      }
    }

    bindGestures() {
      const c = this.$.canvas;
      const pts = new Map();
      let last = null;
      const snapshot = () => {
        const a = [...pts.values()];
        if (a.length === 1) return { x: a[0].x, y: a[0].y, d: 0 };
        const [p, q] = a;
        return { x: (p.x + q.x) / 2, y: (p.y + q.y) / 2, d: Math.hypot(p.x - q.x, p.y - q.y) };
      };
      const hideHint = () => this.$.hint.classList.add('is-gone');
      c.addEventListener('pointerdown', (e) => {
        c.setPointerCapture(e.pointerId);
        pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
        last = snapshot();
        hideHint();
      });
      c.addEventListener('pointermove', (e) => {
        if (!pts.has(e.pointerId)) return;
        pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
        const now = snapshot();
        if (last && pts.size <= 2) {
          const s = this.view.s;
          this.state.x += (now.x - last.x) / s;
          this.state.y += (now.y - last.y) / s;
          if (pts.size === 2 && last.d > 0 && now.d > 0) this.zoom(now.d / last.d);
          this.clamp();
          this.draw();
        }
        last = now;
      });
      const up = (e) => {
        pts.delete(e.pointerId);
        last = pts.size ? snapshot() : null;
      };
      c.addEventListener('pointerup', up);
      c.addEventListener('pointercancel', up);
      c.addEventListener(
        'wheel',
        (e) => {
          e.preventDefault();
          hideHint();
          this.zoom(Math.exp(-e.deltaY * 0.0015));
          this.draw();
        },
        { passive: false }
      );
    }

    zoom(f) {
      const p = this.photo;
      const minPx = p.width / MAX_WALL_CM;
      const maxPx = p.width / MIN_WALL_CM;
      this.state.pxPerCm = Math.min(maxPx, Math.max(minPx, this.state.pxPerCm * f));
    }

    clamp() {
      const p = this.photo;
      this.state.x = Math.min(p.width, Math.max(0, this.state.x));
      this.state.y = Math.min(p.height, Math.max(0, this.state.y));
    }

    async save() {
      track('save');
      const c = document.createElement('canvas');
      c.width = this.photo.width;
      c.height = this.photo.height;
      this.draw(c);
      const blob = await new Promise((r) => c.toBlob(r, 'image/jpeg', 0.9));
      const name = (this.cfg.productTitle || 'poster').replace(/[^\w-]+/g, '-').toLowerCase() + '-wall.jpg';
      const file = new File([blob], name, { type: 'image/jpeg' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: this.cfg.productTitle });
          return;
        } catch (e) {
          if (e && e.name === 'AbortError') return;
        }
      }
      const a = el('a', '', { href: URL.createObjectURL(blob), download: name });
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 4000);
    }

    /** Applies size + frame to the product form, then closes. */
    apply() {
      track('apply');
      const st = this.state;
      const opt = document.querySelector(`.variant-option[data-variant-id="${st.size.id}"]`);
      if (opt && !opt.classList.contains('selected')) opt.click();
      else if (!opt) {
        const input = document.querySelector('form[action*="/cart/add"] [name="id"]');
        if (input) input.value = st.size.id;
      }
      // The frame selector re-evaluates its swatches on variant change; pick the frame after that.
      setTimeout(() => {
        const sw = [...document.querySelectorAll('.frame-selector__swatch')].find(
          (x) => this.keyOf(x.getAttribute('data-frame')) === st.frame
        );
        if (sw && sw.getAttribute('aria-checked') !== 'true' && sw.getAttribute('aria-disabled') !== 'true') sw.click();
        // Then add to cart with the chosen size and frame, exactly as the PDP button would.
        setTimeout(() => {
          const form = document.querySelector('form[action*="/cart/add"]');
          const btn = form && (form.querySelector('[type="submit"][name="add"]') || form.querySelector('[type="submit"]'));
          if (btn && !btn.disabled) btn.click();
          else if (form && form.requestSubmit) form.requestSubmit();
        }, 250);
      }, 60);
      this.close();
    }

    /* ---------- AR ---------- */
    async startAR() {
      track('ar');
      const t = this.t;
      this.$.start.hidden = true;
      this.$.arView.hidden = false;
      this.$.back.hidden = false;
      this.$.arStatus.textContent = t.arPreparing;
      this.renderChips();
      try {
        await Promise.all([this.preload(), loadModelViewer()]);
        const mv = document.createElement('model-viewer');
        mv.setAttribute('ar', '');
        mv.setAttribute('ar-modes', 'webxr quick-look');
        mv.setAttribute('ar-placement', 'wall');
        mv.setAttribute('ar-scale', 'fixed');
        mv.setAttribute('camera-controls', '');
        mv.setAttribute('shadow-intensity', '0.6');
        mv.setAttribute('alt', this.cfg.productTitle || '');
        mv.className = 'wall-preview__mv';
        mv.appendChild(el('span', 'wall-preview__ar-slot', { slot: 'ar-button', hidden: '' }));
        mv.addEventListener('ar-status', (e) => {
          if (e.detail && e.detail.status === 'failed') this.$.arStatus.textContent = t.arFailed;
        });
        this.$.arStage.innerHTML = '';
        this.$.arStage.appendChild(mv);
        this.mv = mv;
        await this.updateARModel();
      } catch (e) {
        console.warn('[wall-preview] AR failed', e);
        this.$.arStatus.textContent = t.arFailed;
      }
    }

    /** Builds the GLB for the current size + frame (real size in metres) and loads it. */
    async updateARModel() {
      const mv = this.mv;
      if (!mv) return;
      const t = this.t;
      const run = (this._arRun = (this._arRun || 0) + 1);
      const st = this.state;
      this.$.arOpenLabel.innerHTML = '';
      this.$.arOpenLabel.append(t.arOpen, el('small', 'wall-preview__btn-sub', { text: this.optionLabel() }));
      try {
        const art = this.compose(st.size, st.frame, 2048);
        const [ow, oh] = this.outerCm(st.size, st.frame);
        const framed = st.frame !== 'none' && this.frameAllowed(st.size);
        const f = this.cfg.frames.find((x) => x.key === st.frame) || {};
        const glb = await buildGlb(art, {
          w: ow / 100,
          h: oh / 100,
          band: framed ? this.bandCm(st.size) / 100 : 0,
          depth: (Number((this.cfg.frameCm || {}).depth) || 2) / 100,
          swatch: f.side || f.swatch
        });
        if (run !== this._arRun || mv !== this.mv) return; // a newer choice won
        const loaded = new Promise((resolve, reject) => {
          mv.addEventListener('load', resolve, { once: true });
          mv.addEventListener('error', reject, { once: true });
        });
        const old = this._arUrl;
        this._arUrl = URL.createObjectURL(glb);
        mv.setAttribute('src', this._arUrl);
        await loaded;
        if (old) URL.revokeObjectURL(old);
        if (run !== this._arRun) return;
        this.$.arStatus.textContent = mv.canActivateAR ? t.arHint : t.arFailed;
        this.$.arOpen.hidden = !mv.canActivateAR;
      } catch (e) {
        console.warn('[wall-preview] AR model failed', e);
        if (run === this._arRun) this.$.arStatus.textContent = t.arFailed;
      }
    }
  }

  customElements.define('wall-preview', WallPreview);
})();
